/**
 * Задание stylepack/next-back-pair (слово владельца 21.10.2026, Mojo) —
 * ЗЕРКАЛО кабинета полей кнопки «назад» в одном ряду с «далее» (блок
 * `button_next`).
 *
 * Канон — сервер `backend/src/ai-funnels/block-schema/next-back-pair.ts`;
 * границы, умолчания и перечень цветов здесь те же. Разъезд в обе стороны
 * ловит сторож `next-back-pair.ends.spec.ts` (читает этот файл с диска).
 *
 * Модуль едет в первом экране покупателя (его читает доля ядра
 * `core-blocks/NextBackPairRenderer.tsx`), поэтому без импортов кабинета.
 */
import type { CSSProperties } from 'react';
import type { EditorBlock } from '@/store/editorSlice';

export const NEXT_BACK_PAIR_BOUNDS = {
  backBesideWidth: { min: 32, max: 120, default: 56 },
  backBesideGap: { min: 0, max: 40, default: 12 },
  backBesideRadius: { min: 0, max: 60 },
} as const;

export const NEXT_BACK_PAIR_COLOR_FIELDS = [
  'backBesideBg',
  'backBesideIconColor',
  'backBesideBorderColor',
] as const;

/** Код цвета — то же правило, что BLOCK_COLOR_PATTERN сервера. */
export const NEXT_BACK_PAIR_COLOR_PATTERN =
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Вид ряда после зажима; поля без значения — как у кнопки «далее». */
export interface NextBackPairLook {
  width: number;
  gap: number;
  radius?: number;
  bg?: string;
  iconColor?: string;
  borderColor?: string;
}

function clampInt(
  value: unknown,
  min: number,
  max: number,
): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(max, Math.max(min, Math.round(value)));
}

function color(value: unknown): string | undefined {
  return typeof value === 'string' && NEXT_BACK_PAIR_COLOR_PATTERN.test(value)
    ? value
    : undefined;
}

/**
 * Поля блока → вид ряда. Нет `backBeside: true` — `null` (ряда нет).
 * Число за границей прижимается к границе, не число — умолчание; негодный
 * цвет снимается.
 */
export function readNextBackPair(block: EditorBlock): NextBackPairLook | null {
  const b = block as unknown as Record<string, unknown>;
  if (b.backBeside !== true) return null;
  const W = NEXT_BACK_PAIR_BOUNDS.backBesideWidth;
  const G = NEXT_BACK_PAIR_BOUNDS.backBesideGap;
  const R = NEXT_BACK_PAIR_BOUNDS.backBesideRadius;
  const look: NextBackPairLook = {
    width: clampInt(b.backBesideWidth, W.min, W.max) ?? W.default,
    gap: clampInt(b.backBesideGap, G.min, G.max) ?? G.default,
  };
  const radius = clampInt(b.backBesideRadius, R.min, R.max);
  if (radius !== undefined) look.radius = radius;
  const bg = color(b.backBesideBg);
  if (bg) look.bg = bg;
  const iconColor = color(b.backBesideIconColor);
  if (iconColor) look.iconColor = iconColor;
  const borderColor = color(b.backBesideBorderColor);
  if (borderColor) look.borderColor = borderColor;
  return look;
}

/**
 * Стили ряда: строка (flex, промежуток), кнопка «назад» (своя ширина, высота
 * растягивается по «далее»; без полей — прозрачный фон, рамка 1 px и стрелка
 * цветом текста «далее», скругление «далее») и ячейка «далее» на остальную
 * ширину.
 */
export function nextBackPairStyles(
  look: NextBackPairLook,
  nextStyle: CSSProperties,
): { row: CSSProperties; back: CSSProperties; slot: CSSProperties } {
  const nextColor =
    typeof nextStyle.color === 'string' && nextStyle.color
      ? nextStyle.color
      : 'currentColor';
  const radius = look.radius ?? nextStyle.borderRadius;
  return {
    row: { display: 'flex', alignItems: 'stretch', gap: look.gap },
    back: {
      width: look.width,
      flex: `0 0 ${look.width}px`,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      margin: 0,
      background: look.bg ?? 'transparent',
      border: `1px solid ${look.borderColor ?? nextColor}`,
      ...(radius !== undefined ? { borderRadius: radius } : {}),
      color: look.iconColor ?? nextColor,
      fontSize: 16,
      lineHeight: 1,
      cursor: 'pointer',
    },
    slot: { flex: '1 1 0%', minWidth: 0 },
  };
}
